import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import useAddOnQuotes from '@/hooks/useAddOnQuotes';
import { sortProvidersByRate, sortQuotesByPremiumRate } from '@/services/utils';
import type { QuoteItem } from '@/types/api';
import { SupportedProductType } from '@/types/api';
import type { OccupationalHealthServiceQuote, Quote } from '@/types/app';
import { getProductLabel } from '../utils/productLabels';

export type RequestQuoteGroupKind = 'insurance' | 'ohs';

/**
 * All quotes for one product type, plus which one the customer selected. Carries
 * the raw quote objects so the request overview can render the flow's own quote
 * cards (`cards/Quote` for insurance, `cards/Provider` for occupational health)
 * unchanged, in read-only mode.
 */
export interface RequestQuoteGroup {
	key: string;
	label: string;
	/** Product type string used by the insurance card for its document links. */
	productType: string;
	kind: RequestQuoteGroupKind;
	insuranceQuotes: QuoteItem[];
	ohsQuotes: OccupationalHealthServiceQuote[];
	selectedQuoteId: string | null;
}

export interface UseRequestQuotesResult {
	groups: RequestQuoteGroup[];
	/** Raw per-product lists, as `buildProductEntries` wants them for the package overview. */
	quoteLists: {
		occupationalHealthServiceQuotes: OccupationalHealthServiceQuote[];
		wgaExtendedQuotes: QuoteItem[];
		wgaQuotes: QuoteItem[];
		wiaExcessQuotes: QuoteItem[];
	};
	isLoading: boolean;
	error: string | null;
	refetch: () => void;
}

/** Bridges the `AppData.quotes` `Quote` shape to the `QuoteItem` the flow card expects. */
const toQuoteItem = (quote: Quote): QuoteItem => ({
	quote_id: quote.quote_id,
	premium_rate_percentage: quote.premium_rate_percentage ?? 0,
	amount: quote.amount,
	total: quote.total,
	saving_percentage: quote.saving_percentage,
	saving_amount: quote.saving_amount,
	insurer: quote.insurer,
	insurer_name: quote.insurer_name,
	image_key: typeof quote.image_key === 'string' ? quote.image_key : null
});

/**
 * Read-only fetch of every relevant product type's quotes for the request
 * overview. Unlike the customer flow it never PATCH-triggers a new quote run —
 * it only GETs what already exists, mirroring `Overview/Summary`'s fetch. Each
 * group carries the raw quotes plus the customer's selected quote id so the page
 * can render the flow cards read-only and highlight the selection.
 */
const useRequestQuotes = (id: string | undefined): UseRequestQuotesResult => {
	const { t } = useTranslation();
	const { appData, applicationData } = useAppContext();

	const productTypes = useMemo<SupportedProductType[]>(() => {
		const raw = appData?.relevant_product_types ?? [];
		const allowed = new Set<string>(Object.values(SupportedProductType));
		return raw.filter((pt): pt is SupportedProductType => allowed.has(pt));
	}, [appData?.relevant_product_types]);

	const {
		occupationalHealthServiceQuotes,
		wgaExtendedQuotes,
		wgaQuotes,
		wiaExcessQuotes,
		isLoading,
		error: fetchError,
		refetch
	} = useAddOnQuotes(id, productTypes);

	// Raw fetch/network messages are never shown to the reader.
	const error = fetchError != null ? t('requestOverview.quotes.loadError') : null;

	const groups = useMemo<RequestQuoteGroup[]>(() => {
		const insuranceGroup = (
			key: SupportedProductType,
			quotes: QuoteItem[],
			selectedQuoteId: string | null
		): RequestQuoteGroup => ({
			key,
			label: getProductLabel(t, key),
			productType: key,
			kind: 'insurance',
			// Cheapest first, matching the customer flow's carousels.
			insuranceQuotes: sortQuotesByPremiumRate(quotes),
			ohsQuotes: [],
			selectedQuoteId
		});

		const result: RequestQuoteGroup[] = [];
		const sickLeaveQuotes = (appData?.quotes?.sick_leave_short_term ?? []).map(toQuoteItem);

		// Short-term sick leave — always the primary product.
		result.push(
			insuranceGroup(
				SupportedProductType.SICK_LEAVE_SHORT_TERM,
				sickLeaveQuotes,
				applicationData.selected_sick_leave_short_term_quote_id
			)
		);

		if (productTypes.includes(SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE)) {
			result.push({
				key: SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE,
				label: getProductLabel(t, SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE),
				productType: SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE,
				kind: 'ohs',
				insuranceQuotes: [],
				ohsQuotes: sortProvidersByRate(occupationalHealthServiceQuotes),
				selectedQuoteId: applicationData.selected_occupational_health_service_quote_id
			});
		}

		if (productTypes.includes(SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED)) {
			result.push(
				insuranceGroup(
					SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED,
					wgaExtendedQuotes,
					applicationData.selected_wga_gap_extended_quote_id
				)
			);
		}

		if (productTypes.includes(SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP)) {
			result.push(
				insuranceGroup(
					SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP,
					wgaQuotes,
					applicationData.selected_wga_gap_quote_id
				)
			);
		}

		if (productTypes.includes(SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS)) {
			result.push(
				insuranceGroup(
					SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS,
					wiaExcessQuotes,
					applicationData.selected_wia_excess_quote_id
				)
			);
		}

		return result;
	}, [
		appData?.quotes?.sick_leave_short_term,
		applicationData,
		productTypes,
		occupationalHealthServiceQuotes,
		wgaExtendedQuotes,
		wgaQuotes,
		wiaExcessQuotes,
		t
	]);

	const quoteLists = useMemo(
		() => ({
			occupationalHealthServiceQuotes,
			wgaExtendedQuotes,
			wgaQuotes,
			wiaExcessQuotes
		}),
		[occupationalHealthServiceQuotes, wgaExtendedQuotes, wgaQuotes, wiaExcessQuotes]
	);

	return { groups, quoteLists, isLoading, error, refetch };
};

export default useRequestQuotes;
